import { Box, Button, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { principlePresentation } from "@chessla/shared";
import { layoutTokens, typographyTokens } from "@/shared/theme";
import { PatternEvidenceLesson } from "../components";
import { motifBeginnerExplanation, nextBoardHabit, shortDate } from "../pattern-copy";
import type { PatternExplorerItem } from "../pattern-explorer";
import type { FocusCohort, PatternExample } from "../patterns.api";
import { explorerItemTitle } from "./PatternExplorerRow";

/** Board side fits one screen: workspace height minus the compact header, evidence switcher, and move controls. */
export const PANEL_BOARD_MAX = "max(280px, min(100%, calc(100dvh - 230px)))";

const labelSx = {
  fontSize: "caption.fontSize",
  fontWeight: "fontWeightBold",
  letterSpacing: typographyTokens.tracking.label,
} as const;

/** The single evidence panel: what the item is, its numbers, the habit, then the board. */
export const PatternExplorerPanel = ({
  item,
  format,
  exactTimeControl,
  analyzedGames,
  onReview,
}: {
  item: PatternExplorerItem;
  format: FocusCohort;
  exactTimeControl?: string;
  analyzedGames: number;
  onReview: (example: PatternExample) => void;
}) => {
  if (item.kind === "moment")
    return <MomentPanel item={item} format={format} exactTimeControl={exactTimeControl} onReview={onReview} />;
  const tactical = item.kind === "tactical" ? item.pattern : null;
  const principle = item.kind === "principle" ? item.pattern : null;
  const presentation = principle ? principlePresentation(principle.principle) : null;
  const description = tactical ? motifBeginnerExplanation(tactical.motif) : (presentation?.traditionalName ?? "");
  const habit = tactical ? nextBoardHabit(tactical.motif) : (presentation?.cue ?? "");
  const games = tactical ? tactical.gameCount : (principle?.affectedGames ?? 0);
  const occurrences = tactical ? tactical.count : (principle?.volume.occurrences ?? 0);
  const positioned = tactical
    ? tactical.examples.some((example) => example.fenBefore)
    : Boolean(principle?.examples.length);
  const firstGame = (tactical?.examples[0] ?? principle?.examples[0])?.gameId;
  return (
    <Box sx={{ border: layoutTokens.dividerBorder, borderColor: "divider", bgcolor: "background.paper" }}>
      <PanelHeader
        label={`${tactical ? "TACTICAL PATTERN" : "PRINCIPLE"} / REAL EVIDENCE`}
        title={explorerItemTitle(item)}
        facts={`${games} of ${item.pattern.volume?.analyzedGames ?? analyzedGames} games · ${occurrences} verified moments${
          tactical ? ` · last seen ${shortDate(tactical.lastSeenAt)}` : ""
        }.`}
      />
      {positioned ? (
        <PatternEvidenceLesson
          format={format}
          exactTimeControl={exactTimeControl}
          motif={tactical ? tactical.motif : (principle?.principle ?? item.id)}
          description={description}
          habit={habit}
          occurrences={occurrences}
          affectedGames={games}
          examples={tactical ? tactical.examples : []}
          principleExample={principle?.examples[0]}
          onReview={onReview}
          inPanel
          boardMaxWidth={PANEL_BOARD_MAX}
        />
      ) : (
        <Box sx={{ p: 2.5 }}>
          <Typography>No saved position for this pattern yet.</Typography>
          {firstGame && (
            <Button component={RouterLink} to={`/games/${firstGame}`} sx={{ mt: 1 }}>
              Open the game
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

const MomentPanel = ({
  item,
  format,
  exactTimeControl,
  onReview,
}: {
  item: Extract<PatternExplorerItem, { kind: "moment" }>;
  format: FocusCohort;
  exactTimeControl?: string;
  onReview: (example: PatternExample) => void;
}) => {
  const habit = "Replay the position and find the safer move before looking at the answer.";
  return (
    <Box sx={{ border: layoutTokens.dividerBorder, borderColor: "divider", bgcolor: "background.paper" }}>
      <PanelHeader label="MOMENT / REAL EVIDENCE" title={item.title} facts={item.note} />
      <PatternEvidenceLesson
        format={format}
        exactTimeControl={exactTimeControl}
        motif={item.id}
        description={item.note}
        habit={habit}
        occurrences={item.examples.length}
        affectedGames={new Set(item.examples.map((example) => example.gameId)).size}
        examples={item.examples}
        onReview={onReview}
        inPanel
        boardMaxWidth={PANEL_BOARD_MAX}
      />
    </Box>
  );
};

/** Compact: the board, not the heading, gets the screen height. The description and board habit appear with the evidence. */
const PanelHeader = ({ label, title, facts }: { label: string; title: string; facts: string }) => (
  <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.25, borderBottom: layoutTokens.dividerBorder, borderColor: "divider" }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
      <Typography
        component="h2"
        sx={{ fontSize: "h5.fontSize", fontWeight: "fontWeightBold", letterSpacing: typographyTokens.tracking.heading }}
      >
        {title}
      </Typography>
      <Typography sx={{ ...labelSx, color: "secondary.main" }}>{label}</Typography>
    </Box>
    <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: "body2.fontSize" }}>{facts}</Typography>
  </Box>
);
